'use client';

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useLocale } from 'next-intl';
import { TIMEZONE_COOKIE } from '@/lib/design-greeting-server';
import type { BillingPlanKey } from '@/lib/billing-plan-ui';
import { useIsRenderIdentity } from '@/providers/render-identity';

/** The greeting selected for this viewer's first paint. */
export type ServerDesignGreeting = {
  greeting: string;
  userName: string;
  userId: string;
  source: 'ai' | 'fallback';
  /** The locale the server rendered `greeting` in. */
  locale: string;
  billingPlan?: BillingPlanKey;
};

const ServerDesignGreetingContext = createContext<ServerDesignGreeting | null>(
  null
);

export function ServerDesignGreetingProvider({
  greeting,
  children,
}: {
  greeting: ServerDesignGreeting | null;
  children: ReactNode;
}) {
  const locale = useLocale();
  const renderIdentity = useIsRenderIdentity(greeting?.userId ?? null);
  // A payload from an earlier locale (stale RSC state after a language
  // switch) must not reach the headline; the client picks current copy.
  const usable = renderIdentity && greeting?.locale === locale;
  return (
    <ServerDesignGreetingContext.Provider value={usable ? greeting : null}>
      {children}
    </ServerDesignGreetingContext.Provider>
  );
}

export function useServerDesignGreeting(): ServerDesignGreeting | null {
  return useContext(ServerDesignGreetingContext);
}

/** Lets the server compute the viewer's local greeting hour next time. */
export function TimezoneCookieSync() {
  useEffect(() => {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!timeZone) return;
      document.cookie = `${TIMEZONE_COOKIE}=${encodeURIComponent(timeZone)}; Path=/; Max-Age=31536000; SameSite=Lax`;
    } catch {
      /* Cookies may be unavailable; the client greeting path still runs. */
    }
  }, []);
  return null;
}
